import React, { PureComponent, MouseEvent } from "react";
import ImageIcon from "components/icons/image";
import { $, merge } from "glamor";
import _ from "lodash";
import theme from "styles/theme";
import { ICellRendererParams, RowNode } from "ag-grid-community";
import { formatMessage } from "lib/globalize-util";
import {
    applyFormatters,
    isSmallImageShown,
    ProductRequiredDisplayFields,
} from "helpers/legacy-cart/line-items/view-definition-config-helper";
import { getProductVariantAvailableDays } from "../../helpers";
import { getProductLink, postGotoProduct } from "helpers/legacy-cart/messaging/messaging-helper";
import { getRange } from "helpers/legacy-cart/line-items/items-conversion-helper";
import type { IItem } from "components/shipment-review/types";
import { SECTIONS } from "types/view-definitions";

const defaultLocale = "en";

interface Props {
    brandId?: string;
}

export class GroupRenderer extends PureComponent<ICellRendererParams & Props> {
    styles = merge(
        { fontFamily: "Lato !important" },
        $(" .groupContainer", {
            display: "flex",
            height: "100%",
            width: "100%",
        }),
        $(" .groupContainer > .description", {
            flex: "1",
            width: "25%",
            padding: "4px 4px 4px 0",
        }),
        $(" img", {
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
        }),
        $(" .noImageIcon", {
            height: "85px !important",
            width: "58px !important",
            color: `${theme.palette.grey} !important`,
            padding: "5px 8px 5px 0",
        }),
        $(" .name", {
            fontSize: "12px",
            fontWeight: "bold",
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: "1.58",
            letterSpacing: "normal",
            color: theme.palette.darkerGrey,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            height: "19px",
        }),
        $(" .name:hover", {
            color: theme.palette.primary1Color,
        }),
        $(" .attributes", {
            fontSize: "11px",
            fontWeight: "normal",
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: "1.45",
            letterSpacing: "normal",
            color: theme.palette.darkGrey,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            height: "16px",
        }),
        $(" .singleValue", {
            paddingLeft: "8px",
        }),
        $(" .notationText", {
            fontSize: "10px",
            fontWeight: "normal",
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: "1.6",
            letterSpacing: "normal",
            color: theme.palette.darkGrey,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            height: "16px",
        }),
        $(" .notationText .text", {
            color: theme.palette.secondaryTextColor,
            paddingRight: "2px",
        }),
        $(" .price", {
            fontSize: "12px",
            fontWeight: "bold",
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: "1.58",
            letterSpacing: "normal",
            height: "19px",
            color: theme.palette.darkerGrey,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            paddingTop: "1px",
        }),
        $(" .availableFrom", {
            paddingRight: "2px",
        }),
        $(" .availableUntil", {
            paddingLeft: "2px",
        }),
        $(" .imageContainer", {
            width: "51px",
            margin: "8px 8px 8px 0",
        })
    );

    isExpandable = () => {
        const rowNode = this.props.node;
        // @ts-ignore
        const reducedLeafNode = this.props.columnApi.isPivotMode() && rowNode.leafGroup;
        return rowNode.isExpandable() && !rowNode.footer && !reducedLeafNode;
    };

    onExpandOrContract = () => {
        const rowNode: RowNode = this.props.node;
        rowNode.setExpanded(!rowNode.expanded);
        this.forceUpdate();
    };

    onClickProductName = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const { node, brandId } = this.props;
        const {
            item: { product },
        } = node.allLeafChildren?.[0]?.data ?? node.data;
        postGotoProduct(brandId, product._id);
    };

    render() {
        const { node, brandId } = this.props;
        const { allLeafChildren } = node;
        const rowData: IItem = allLeafChildren?.[0]?.data ?? node.data;

        if (!rowData) {
            return null;
        }

        const {
            itemView,
            currency,
            item,
            item: { warehouse, product },
            defaultDeliveryWindow,
        } = rowData;
        const imageUrl = _.head(product.imagesT);
        const items = allLeafChildren?.map(({ data }) => data.item) ?? [];
        const isExpandable = this.isExpandable();
        const showExpandable = false;
        const [min, max] = getRange(_.map(items, ({ pricing }) => pricing?.wholesale ?? 0));
        const priceRanges = max === null ? "" : max === min ? `${max}` : `${min} - ${max}`;

        const availableDays = getProductVariantAvailableDays(item.product, undefined, defaultDeliveryWindow);
        const viewDefFields = _.keyBy(itemView?.sections?.[SECTIONS.DETAILS]?.elements, "reference");
        const showImage = isSmallImageShown(itemView);
        const productLink = getProductLink(brandId, product._id);

        // only product fields supported
        const fields = _.reject(
            _.map(viewDefFields, (field, key) => ({
                key,
                value: applyFormatters(product?.[key] || field.default_value, field),
            })),
            { key: "name" }
        );

        return (
            <div style={{ paddingLeft: `${node.level * 20}px` }} {...this.styles}>
                {isExpandable && (
                    <div className="groupContainer">
                        {!node.expanded && showExpandable && (
                            <span className="ag-group-contracted" ref="eContracted" onClick={this.onExpandOrContract}>
                                <span className="ag-icon ag-icon-expanded" unselectable="on" />
                            </span>
                        )}
                        {node.expanded && showExpandable && (
                            <span className="ag-group-expanded" ref="eExpanded" onClick={this.onExpandOrContract}>
                                <span className="ag-icon ag-icon-contracted" unselectable="on" />
                            </span>
                        )}
                        {node.level === 0 && (
                            <>
                                {showImage && (
                                    <div className="imageContainer">
                                        {imageUrl && <img src={imageUrl} />}
                                        {!imageUrl && <ImageIcon className="noImageIcon" />}
                                    </div>
                                )}
                                <div className="description">
                                    {viewDefFields[ProductRequiredDisplayFields.NAME] && (
                                        <a href={productLink} className="name" onClick={this.onClickProductName}>
                                            {product.name}
                                        </a>
                                    )}

                                    {fields.map(({ key, value }) => (
                                        <div key={key} className="attributes">
                                            {value}
                                        </div>
                                    ))}

                                    <div className="price">
                                        {priceRanges && currency?.wholesale?.symbol}
                                        {priceRanges}
                                    </div>
                                    <div className="notationText">
                                        <span className="text">{formatMessage("Avail from:")}</span>
                                        <span className="availableFrom">
                                            {availableDays.from.utc().locale(defaultLocale).format("MM/DD/YY")}
                                        </span>
                                        -
                                        <span className="availableUntil">
                                            {availableDays.until
                                                ? availableDays.until.utc().locale(defaultLocale).format("MM/DD/YY")
                                                : ""}
                                        </span>
                                    </div>
                                    <div className="notationText">
                                        <span className="text">
                                            {formatMessage(item.prebook ? "Prebook" : "Immediate")}
                                        </span>
                                    </div>
                                    {warehouse && (
                                        <div className="notationText">
                                            <span className="text">{`${formatMessage("Warehouse")} ${
                                                warehouse.name
                                            }`}</span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        {node.level !== 0 && <div className="singleValue">{this.props.value}</div>}
                    </div>
                )}
                {!isExpandable && showImage && (
                    <div>
                        {imageUrl && <img src={imageUrl} />}
                        {!imageUrl && <ImageIcon className="noImageIcon" />}
                    </div>
                )}
            </div>
        );
    }
}

export const GroupContainer = GroupRenderer;
